import { useCallback, useEffect, useMemo, useState } from 'react';
import { REFRESH_DELAY_MS } from '../config/ServiceManager.config';
import {
  addServiceOp,
  fetchServicesOp,
  performServiceActionOp,
  removeServiceOp,
} from '../ops/ServiceManager.ops';
import type {
  EllipsisOpenState,
  PendingAction,
  RawService,
  ServiceAction,
  ServiceRecord,
} from '../types/ServiceManager.types';
import { filterServicesBySearch, getStatusFromAction } from '../utils/ServiceManager.utils';

/** Options accepted by the {@link useServices} hook. */
interface UseServicesOptions {
  search?: string;
  /**
   * Show every deployment's services rather than only the caller's workplace.
   *
   * Scoping is the server's job: the Fusion proxy filters by the workplace id
   * on the verified token and always includes globally-scoped rows. This flag
   * sends `?all=true` to bypass that, which is what an operator managing more
   * than one deployment on the same supervisor needs.
   */
  allWorkplaces?: boolean;
}

/** Return value of the {@link useServices} hook. */
interface UseServicesReturn {
  // State
  services: ServiceRecord[];
  filteredServices: ServiceRecord[];
  /** Globally-scoped services rendered in the bottom section. Search applies to these too. */
  filteredGlobalServices: ServiceRecord[];
  removedServices: RawService[];
  isLoading: boolean;
  isRefreshing: boolean;
  reqTime: Date;
  pendingAction: PendingAction | null;
  rowIdEllipsisOpen: EllipsisOpenState;
  mobileRowIdEllipsisOpen: EllipsisOpenState;

  // Actions
  fetchServices: (options?: { showSpinner?: boolean }) => Promise<void>;
  handleActionChange: (service: ServiceRecord, action: ServiceAction) => void;
  handleActionConfirm: (reason: string) => void;
  handleActionCancel: () => void;
  handleAddService: (serviceName: string) => Promise<void>;
  handleRemoveService: (service: ServiceRecord) => Promise<void>;
  setRowIdEllipsisOpen: React.Dispatch<React.SetStateAction<EllipsisOpenState>>;
  setMobileRowIdEllipsisOpen: React.Dispatch<React.SetStateAction<EllipsisOpenState>>;
}

/**
 * Hook that manages service list state, actions, and ellipsis menu toggles.
 *
 * Note that this list only ever contains services the supervisor is
 * configured to run. A service whose enable flag is off has no registry row,
 * so it cannot appear here and cannot be started from here. Turning one on
 * belongs to the Deployments tab's catalog.
 *
 * @param options - Search term and workplace scope.
 * @returns State values and action handlers for the ServiceManager page.
 */
export const useServices = ({
  search = '',
  allWorkplaces = false,
}: UseServicesOptions = {}): UseServicesReturn => {
  const [services, setServices] = useState<ServiceRecord[]>([]);
  const [globalServices, setGlobalServices] = useState<ServiceRecord[]>([]);
  const [removedServices, setRemovedServices] = useState<RawService[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reqTime, setReqTime] = useState<Date>(() => new Date());
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [rowIdEllipsisOpen, setRowIdEllipsisOpen] = useState<EllipsisOpenState>({});
  const [mobileRowIdEllipsisOpen, setMobileRowIdEllipsisOpen] = useState<EllipsisOpenState>({});

  const fetchServices = useCallback(
    async ({ showSpinner = true } = {}) => {
      try {
        if (showSpinner) setIsLoading(true);
        else setIsRefreshing(true);

        const result = await fetchServicesOp({ all: allWorkplaces });
        setServices(result.services);
        setGlobalServices(result.globalServices);
        setRemovedServices(result.removedServices);
      } catch (err) {
        console.error('Failed to load services:', err);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
        setReqTime(new Date());
      }
    },
    [allWorkplaces],
  );

  // Initial load
  useEffect(() => {
    fetchServices({ showSpinner: true });
  }, [fetchServices]);

  // Filter by search
  const filteredServices = useMemo(() => {
    return filterServicesBySearch(services, search);
  }, [search, services]);

  const filteredGlobalServices = useMemo(() => {
    return filterServicesBySearch(globalServices, search);
  }, [search, globalServices]);

  // Action handlers
  const handleActionChange = useCallback((service: ServiceRecord, action: ServiceAction) => {
    setPendingAction({ service, action });
  }, []);

  const handleActionConfirm = useCallback(
    (reason: string) => {
      if (!pendingAction) return;

      const { service, action } = pendingAction;
      setPendingAction(null);

      // Optimistic update
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id ? { ...item, status: getStatusFromAction(action) } : item,
        ),
      );

      // Perform action and refresh
      performServiceActionOp(service, action, reason)
        .then(() => {
          setTimeout(() => {
            fetchServices({ showSpinner: false });
          }, REFRESH_DELAY_MS);
        })
        .catch((err) => {
          console.error(`Error performing '${action}' on '${service.name}':`, err);
        });
    },
    [pendingAction, fetchServices],
  );

  const handleActionCancel = useCallback(() => {
    setPendingAction(null);
  }, []);

  const handleAddService = useCallback(
    async (serviceName: string) => {
      if (!serviceName) return;

      const template = removedServices.find((s) => s.name === serviceName);
      if (!template) return;

      try {
        await addServiceOp(serviceName);
        setTimeout(() => {
          fetchServices({ showSpinner: false });
        }, REFRESH_DELAY_MS);
      } catch (err) {
        console.error(`Error adding service '${serviceName}':`, err);
      }
    },
    [removedServices, fetchServices],
  );

  const handleRemoveService = useCallback(
    async (service: ServiceRecord) => {
      try {
        await removeServiceOp(service.id);

        setRowIdEllipsisOpen((prev) => {
          const copy = { ...prev };
          delete copy[service.id];
          return copy;
        });

        setMobileRowIdEllipsisOpen((prev) => {
          const copy = { ...prev };
          delete copy[service.id];
          return copy;
        });

        setTimeout(() => {
          fetchServices({ showSpinner: false });
        }, REFRESH_DELAY_MS);
      } catch (err) {
        console.error('Error removing service:', err);
      }
    },
    [fetchServices],
  );

  return {
    services,
    filteredServices,
    filteredGlobalServices,
    removedServices,
    isLoading,
    isRefreshing,
    reqTime,
    pendingAction,
    rowIdEllipsisOpen,
    mobileRowIdEllipsisOpen,
    fetchServices,
    handleActionChange,
    handleActionConfirm,
    handleActionCancel,
    handleAddService,
    handleRemoveService,
    setRowIdEllipsisOpen,
    setMobileRowIdEllipsisOpen,
  };
};
